// src/main/content/contests/ContestDetails.js
//
// Single-contest leaderboard view.
//
// Layout:
//   1. Hero: a 3-column row — title + description on the left (span 2) and a
//      live countdown card on the right (span 1).
//   2. A 4-card stat row underneath the hero: Metric, Duration, Participants,
//      Winners. Equal-weight tiles instead of a sidebar table.
//   3. Optional "Your standing" promoted card if the requesting user is on
//      the board.
//   4. Two-column main area: leaderboard on the left, Rewards sidebar on the
//      right (when reward_description is set).
//
// The leaderboard endpoint serves standings the backend refreshes every 4
// hours (the card shows how long ago), so this component just renders what
// comes back. The endpoint also returns a fresh contest snapshot — we prefer
// that over the (possibly stale) `contest` prop the list view passes in.

import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { DraftBadge, isHitTargetContest, winnersLabel } from "./ContestList";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import GroupsIcon from "@mui/icons-material/Groups";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import BoltIcon from "@mui/icons-material/Bolt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";

import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_contest_leaderboard,
  API_get_contest_timeline,
} from "../../../constants";
import { formatDateReadable } from "../requests/utilities/helpers";
import { formatName } from "../../../helpers/helpers";

import ContestCumulativeChart from "./ContestCumulativeChart";
import ContestRaceReplay from "./ContestRaceReplay";
import ContestTargetRace from "./ContestTargetRace";
import ContestDuplicateModal from "./ContestDuplicateModal";
import { useIsTruncated } from "./useIsTruncated";
import {
  ContestKeyMoments,
  ContestBiggestMovers,
} from "./ContestMomentsAndMovers";

// ---------- formatting ----------

// Goal metrics rank percent of each location's goal; 100 = goal reached.
const GOAL_PERCENT = 100;

function formatMetricValue(value, format) {
  if (value === null || value === undefined) return "—";
  const v = Number(value);
  if (Number.isNaN(v)) return "—";
  if (format === "dollars") {
    return `$${v.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  }
  if (format === "percentage") {
    return `${(v * 100).toFixed(1)}%`;
  }
  if (format === "goal_percent") {
    // Percent of each location's own goal. Floored so a store at 99.6%
    // never reads "100%" before it has actually reached its goal.
    return `${Math.floor(v)}%`;
  }
  if (format === "points_delta") {
    // Signed percentage-point delta vs a target, e.g. "−2.8 pts" (under) /
    // "+1.2 pts" (over). toFixed carries the minus sign; we add "+" for
    // positives so "on-target-ish but over" reads clearly.
    return `${v > 0 ? "+" : ""}${v.toFixed(1)} pts`;
  }
  if (format === "hours") {
    return `${v.toFixed(1)}h`;
  }
  if (format === "rate") {
    // Ratio sales-driver metrics (e.g. "Shakes per Order") live in the 0–1
    // range where the default 1-decimal rounding would collapse 0.46 and
    // 0.52 into "0.5" — always show two decimals.
    return v.toFixed(2);
  }
  return v.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

// ---------- small UI ----------

function StatusPill({ status, daysRemaining }) {
  // Live is violet (brand "look here, happening now"), Completed stays
  // emerald (positive but settled), Upcoming is neutral slate. Mirrors the
  // ContestList card pill so the two views read consistently.
  const cfg = {
    Active: {
      Icon: BoltIcon,
      cls: "bg-violet-50 text-violet-700 border-violet-200",
      label: daysRemaining !== null ? `Live · ${daysRemaining}d remaining` : "Live",
    },
    Upcoming: {
      Icon: ScheduleIcon,
      cls: "bg-slate-50 text-slate-600 border-slate-200",
      label: "Upcoming",
    },
    Completed: {
      Icon: CheckCircleIcon,
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      label: "Completed",
    },
  }[status] || {
    Icon: ScheduleIcon,
    cls: "bg-slate-50 text-slate-600 border-slate-200",
    label: status,
  };
  const Icon = cfg.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${cfg.cls}`}
    >
      <Icon style={{ fontSize: 13 }} />
      {cfg.label}
    </span>
  );
}

// ---------- leaderboard list rows ----------

// Grayed "215 Shakes Sold · 183 Orders" caption for ratio metrics — the raw
// components behind the rate. Rendered only when the backend sent both
// values (ratio contests computed by ratio-aware code); every other metric
// leaves them null and the caption self-hides. For points_delta ratio
// metrics (store vs target) the ranked value is a delta, so the caption
// also appends the actual rate percent ("· 25.8%") — otherwise the real
// attach rate is invisible. Goal metrics carry the location's total and goal
// instead, captioned "$1,240 of $3,000" (``goalValueFormat`` says whether
// they're dollars).
function RatioComponentsCaption({
  row,
  numeratorLabel,
  denominatorLabel,
  format,
  goalValueFormat,
}) {
  if (row.numerator_value == null || row.denominator_value == null) {
    return null;
  }
  const fmt = (v) =>
    Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (format === "goal_percent") {
    const money = goalValueFormat === "dollars" ? "$" : "";
    return (
      <div className="text-[11px] text-slate-400 whitespace-nowrap text-right">
        {money}
        {fmt(row.numerator_value)} of {money}
        {fmt(row.denominator_value)}
      </div>
    );
  }
  const den = Number(row.denominator_value);
  const showRate = format === "points_delta" && den > 0;
  return (
    <div className="text-[11px] text-slate-400 whitespace-nowrap text-right">
      {fmt(row.numerator_value)} {numeratorLabel || "sold"} ·{" "}
      {fmt(row.denominator_value)} {denominatorLabel || "orders"}
      {showRate &&
        ` · ${((Number(row.numerator_value) / den) * 100).toFixed(1)}%`}
    </div>
  );
}

function LeaderboardRow({
  row,
  isYou,
  format,
  showStoreTag,
  numeratorLabel,
  denominatorLabel,
  goalValueFormat,
}) {
  // Rank pill color hierarchy: medal colors for the top 3 take precedence
  // over winner/you/default styling so the podium tiers still read at a
  // glance now that we've dropped the separate podium visual.
  let rankPillCls;
  if (row.rank === 1) rankPillCls = "bg-amber-100 text-amber-700";
  else if (row.rank === 2) rankPillCls = "bg-slate-200 text-slate-700";
  else if (row.rank === 3) rankPillCls = "bg-orange-100 text-orange-700";
  else if (row.is_winner) rankPillCls = "bg-emerald-100 text-emerald-700";
  else if (isYou) rankPillCls = "bg-violet-100 text-violet-700";
  else rankPillCls = "bg-slate-100 text-slate-600";

  return (
    <div
      className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
        isYou
          ? "bg-violet-50 border-violet-200 ring-1 ring-violet-200"
          : row.is_winner
          ? "bg-emerald-50/50 border-emerald-100"
          : "bg-white border-slate-100 hover:border-slate-200"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-md font-semibold text-xs ${rankPillCls}`}
        >
          {row.rank}
        </div>
        <div className="min-w-0 flex-1">
          {/* ``truncate`` belongs on the NAME span, not the outer flex
              container — otherwise long names clip the trophy / "You"
              badge along with themselves. The badges get ``shrink-0`` so
              the flex layout sacrifices the name's width first, never the
              badges. */}
          <div className="text-sm font-medium text-slate-900 flex items-center gap-2 min-w-0">
            <span className="truncate min-w-0">
              {row.store_team_member.full_name}
            </span>
            {isYou && (
              <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full shrink-0">
                You
              </span>
            )}
            {row.is_winner && !isYou && (
              <EmojiEventsIcon
                style={{ fontSize: 14 }}
                className="text-emerald-600 shrink-0"
              />
            )}
          </div>
          {showStoreTag && row.store_team_member.store_name && (
            <div className="text-[11px] text-slate-400 truncate">
              {row.store_team_member.store_name}
            </div>
          )}
        </div>
      </div>
      <div className="shrink-0 ml-2">
        <div className="text-sm font-semibold text-slate-900 text-right">
          {formatMetricValue(row.metric_value, format)}
        </div>
        <RatioComponentsCaption
          row={row}
          numeratorLabel={numeratorLabel}
          denominatorLabel={denominatorLabel}
          format={format}
          goalValueFormat={goalValueFormat}
        />
      </div>
    </div>
  );
}

// ---------- leaderboard card ----------
//
// Extracted so the same render is used whether the leaderboard sits in a
// side-by-side grid (paired with the race replay, 1/3 width) or full-width
// when no timeline is available (percentage metrics).

// Re-renders the caller once a minute while ``enabled`` so a relative label
// like "Updated 5 min ago" keeps counting on a page left open.
function useMinuteTick(enabled) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [enabled]);
}

// "Updated 2 hours ago" for an Active contest's leaderboard. Standings are
// recomputed on a schedule (every 4 hours), not on every view, so this tells
// people how current they are. Null when there's nothing to show.
function updatedAgoLabel(contest) {
  if (contest.status !== "Active" || !contest.last_computed_at) return null;
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(contest.last_computed_at).getTime()) / 60000)
  );
  if (minutes < 1) return "Updated just now";
  if (minutes < 60) return `Updated ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `Updated ${days} day${days === 1 ? "" : "s"} ago`;
}

// True when both detail requests succeeded but were served from different
// leaderboard refreshes. Each response carries the contest's
// last_computed_at at the time it was built.
function resultsFromDifferentRefreshes(lbResult, tlResult) {
  if (lbResult.status !== "fulfilled" || tlResult.status !== "fulfilled") {
    return false;
  }
  const stampOf = (result) =>
    result.value?.data?.redux?.payload?.contest?.last_computed_at ?? null;
  return stampOf(lbResult) !== stampOf(tlResult);
}

function LeaderboardCard({
  contest,
  leaderboard,
  visibleRows,
  yourPosition,
  format,
  canToggleExpand,
  leaderboardExpanded,
  setLeaderboardExpanded,
  hiddenCount,
  showStoreTag,
}) {
  useMinuteTick(contest.status === "Active");
  const updatedLabel = updatedAgoLabel(contest);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-full">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <EmojiEventsIcon className="text-amber-500" style={{ fontSize: 20 }} />
          <h3 className="text-base font-semibold text-slate-900">Leaderboard</h3>
        </div>
        <div className="text-xs text-slate-500 text-right">
          <div>
            {contest.status === "Completed"
              ? "Final standings"
              : isHitTargetContest(contest)
              ? "Everyone who hits target earns rewards"
              : `Top ${contest.winners_count} earn rewards`}
          </div>
          {updatedLabel && <div className="text-slate-400">{updatedLabel}</div>}
        </div>
      </div>

      {contest.metric_unavailable && <MetricUnavailableNotice />}

      {leaderboard.length === 0 ? (
        !contest.metric_unavailable && (
          <LeaderboardEmpty
            status={contest.status}
            startDate={contest.start_date}
          />
        )
      ) : (
        <div className="p-3">
          <div className="space-y-1.5">
            {visibleRows.map((row) => (
              <LeaderboardRow
                key={row.store_team_member.id}
                row={row}
                isYou={
                  !!yourPosition &&
                  yourPosition.store_team_member.id ===
                    row.store_team_member.id
                }
                format={format}
                showStoreTag={showStoreTag}
                numeratorLabel={contest.metric_numerator_label}
                denominatorLabel={contest.metric_denominator_label}
                goalValueFormat={contest.metric_goal_value_format}
              />
            ))}
          </div>

          {canToggleExpand && (
            <button
              onClick={() => setLeaderboardExpanded((v) => !v)}
              className="w-full mt-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50 rounded-lg flex items-center justify-center gap-1.5 transition-colors"
            >
              {leaderboardExpanded ? (
                <>
                  <ExpandLessIcon style={{ fontSize: 18 }} />
                  Show less
                </>
              ) : (
                <>
                  <ExpandMoreIcon style={{ fontSize: 18 }} />
                  Show all {leaderboard.length} ({hiddenCount} more)
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MetricUnavailableNotice() {
  return (
    <div className="px-5 py-4 bg-amber-50 border-b border-amber-100">
      <p className="text-sm font-medium text-amber-900">
        This contest's metric is no longer available
      </p>
      <p className="text-xs text-amber-800 mt-1">
        Standings won't update until it's fixed.
      </p>
    </div>
  );
}

function LeaderboardEmpty({ status, startDate }) {
  if (status === "Upcoming") {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
          <ScheduleIcon className="text-slate-400" style={{ fontSize: 22 }} />
        </div>
        <p className="text-sm font-medium text-slate-700">Not started yet</p>
        <p className="text-xs text-slate-500 mt-1">
          Standings will populate once the contest goes live on{" "}
          {formatDateReadable(startDate)}.
        </p>
      </div>
    );
  }
  if (status === "Active") {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-3">
          <BoltIcon className="text-violet-600" style={{ fontSize: 22 }} />
        </div>
        <p className="text-sm font-medium text-slate-700">No scores yet</p>
        <p className="text-xs text-slate-500 mt-1">
          Be the first on the leaderboard.
        </p>
      </div>
    );
  }
  return (
    <div className="text-center py-12">
      <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
        <EmojiEventsIcon className="text-slate-400" style={{ fontSize: 22 }} />
      </div>
      <p className="text-sm font-medium text-slate-700">No qualifying scores</p>
      <p className="text-xs text-slate-500 mt-1">
        Nobody met the minimum threshold for this contest.
      </p>
    </div>
  );
}

// ---------- countdown ----------

// Live ticker. Re-renders the countdown card every second by bumping a state
// counter. Confined to this component so a parent like the leaderboard list
// doesn't re-render with it.
function useNowEverySecond(enabled) {
  const [now, setNow] = useState(() => Date.now());
  const intervalRef = useRef(null);
  useEffect(() => {
    if (!enabled) return undefined;
    intervalRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled]);
  return now;
}

function splitDuration(diffMs) {
  const clamped = Math.max(0, diffMs);
  const days = Math.floor(clamped / (1000 * 60 * 60 * 24));
  const hours = Math.floor((clamped % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((clamped % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((clamped % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
}


// ---------- hero stat strip ----------

function HeroStat({
  label,
  value,
  subtext = null,
  subtextFullText = null,
  valueTooltip = null,
  allowMultiLine = false,
}) {
  // Tooltip logic — three branches:
  //   1. Explicit ``valueTooltip`` ReactNode (e.g., picked-roster popup) →
  //      always show, content is the rich tooltip.
  //   2. String ``value`` that IS actually truncated → show the full
  //      string on hover so users can recover clipped text.
  //   3. Anything else → no tooltip; hovering a fully-visible value just
  //      to see itself echoed in a popup is noise.
  const [valueRef, valueIsTruncated] = useIsTruncated(value, {
    multiLine: allowMultiLine,
  });
  // ``cursor-help`` reflects "hovering this will show extra info" — apply
  // it whenever a tooltip will actually render (rich tooltip OR
  // truncation-recovery tooltip), not just for the rich case.
  const willShowValueTooltip =
    !!valueTooltip ||
    (valueIsTruncated && typeof value === "string" && !!value);
  const valueEl = (
    <div
      ref={valueRef}
      className={`text-sm font-semibold text-slate-900 mt-0.5 ${
        allowMultiLine ? "line-clamp-2 whitespace-pre-line" : "truncate"
      } ${willShowValueTooltip ? "cursor-help" : ""}`}
    >
      {value}
    </div>
  );
  let wrappedValue = valueEl;
  if (valueTooltip) {
    wrappedValue = (
      <Tooltip title={valueTooltip} arrow placement="top">
        {valueEl}
      </Tooltip>
    );
  } else if (valueIsTruncated && typeof value === "string" && value) {
    wrappedValue = (
      <Tooltip title={value} arrow placement="top">
        {valueEl}
      </Tooltip>
    );
  }

  // Subtext tooltip logic — parallel branches:
  //   1. Explicit ``subtextFullText`` (e.g., "Foo, Bar, Baz" for visible
  //      truncated "Foo · Bar · +1 more") → show the full list.
  //   2. Subtext string that IS actually truncated and no override → show
  //      the subtext itself.
  //   3. Otherwise → no tooltip.
  const [subtextRef, subtextIsTruncated] = useIsTruncated(subtext);
  const subtextEl = (
    <div
      ref={subtextRef}
      className="text-[11px] text-slate-500 mt-0.5 truncate"
    >
      {subtext}
    </div>
  );
  let wrappedSubtext = subtextEl;
  if (subtextFullText) {
    wrappedSubtext = (
      <Tooltip title={subtextFullText} arrow placement="top">
        {subtextEl}
      </Tooltip>
    );
  } else if (subtextIsTruncated && typeof subtext === "string" && subtext) {
    wrappedSubtext = (
      <Tooltip title={subtext} arrow placement="top">
        {subtextEl}
      </Tooltip>
    );
  }

  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
        {label}
      </div>
      {wrappedValue}
      {subtext && wrappedSubtext}
    </div>
  );
}

// Compact "Individual" / "Store vs Store" pill for the hero top row. Mirrors
// ContestList's TypeChip but lives inline here to avoid pulling a tiny
// component into a shared module — both are read-only labels with no
// behavior worth deduplicating.
function HeroTypeChip({ contestType }) {
  const isStore = contestType === "store_vs_store";
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600">
      {isStore ? "Store vs Store" : "Individual"}
    </span>
  );
}

// ---------- hero progress bar (under the countdown) ----------
//
// Thin progress bar that visually answers "how far through the window are
// we?". Lives in the hero top row directly below the live countdown so the
// two read together: the countdown shows time-remaining as a number, the
// bar shows time-elapsed as a position. Fill color matches the status
// pill (violet for Live, emerald for Completed, slate for Upcoming) so the
// hero reads as a unified colored "where we are in this contest" cluster.

function HeroProgressBar({ contest }) {
  // Use millisecond-level math, not whole-day diffs. A whole-day diff
  // would hit 100% at midnight on the final day — while the countdown
  // beside this bar still shows "Xh remaining," giving two contradictory
  // signals in the same header. Treating the contest as starting at the
  // start_date's startOf("day") and ending at end_date's endOf("day")
  // matches the countdown's anchor exactly, so they stay in lockstep.
  const startMs = dayjs(contest.start_date).startOf("day").valueOf();
  const endMs = dayjs(contest.end_date).endOf("day").valueOf();
  const totalMs = Math.max(1, endMs - startMs);
  const elapsedMs = Math.max(0, Math.min(totalMs, Date.now() - startMs));
  const progressPct =
    contest.status === "Completed"
      ? 100
      : contest.status === "Upcoming"
      ? 0
      : Math.round((elapsedMs / totalMs) * 100);

  const fillCls =
    {
      Active: "bg-violet-500",
      Upcoming: "bg-slate-300",
      Completed: "bg-emerald-500",
    }[contest.status] || "bg-slate-300";

  return (
    <div className="w-48">
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full transition-[width] duration-500 ${fillCls}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-slate-400 tabular-nums">
        <span>{dayjs(contest.start_date).format("MMM D")}</span>
        <span>{dayjs(contest.end_date).format("MMM D")}</span>
      </div>
    </div>
  );
}

// ---------- inline live countdown (right-aligned in hero header) ----------

function HeroCountdownInline({ contest }) {
  const isTicking = contest.status === "Active" || contest.status === "Upcoming";
  const now = useNowEverySecond(isTicking);

  if (contest.status === "Completed") {
    return (
      <span className="text-sm text-slate-500">
        Ended {formatDateReadable(contest.end_date)}
      </span>
    );
  }

  const targetMs =
    contest.status === "Upcoming"
      ? dayjs(contest.start_date).startOf("day").valueOf()
      : dayjs(contest.end_date).endOf("day").valueOf();
  const { days, hours, minutes, seconds } = splitDuration(targetMs - now);
  const isUpcoming = contest.status === "Upcoming";
  const labelText = isUpcoming ? "Starts in" : "Time left";
  const valueCls = isUpcoming ? "text-slate-700" : "text-emerald-700";

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
        {labelText}
      </span>
      <span className={`font-bold tabular-nums ${valueCls}`}>
        {days}d {hours}h {minutes}m {String(seconds).padStart(2, "0")}s
      </span>
    </div>
  );
}

// ---------- main ----------

export default function ContestDetails({
  contest: contestProp,
  homeStore,
  storeNames,
  isAdmin,
  onBack,
  onEdit,
}) {
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [contest, setContest] = useState(contestProp);
  const [leaderboard, setLeaderboard] = useState([]);
  const [yourPosition, setYourPosition] = useState(null);
  const [timeline, setTimeline] = useState(null);
  // Starts TRUE: effects run after the first paint, so a false initial
  // value renders one frame of the full page with an empty leaderboard
  // before fetchAll flips it — the "flash of details, then spinner" bug.
  const [loading, setLoading] = useState(true);
  // Leaderboard expansion. Most viewers only care about the top few entries —
  // we cap visible rows at LEADERBOARD_VISIBLE_DEFAULT (8 including the
  // podium top-3) and surface a Show all / Show less toggle when there are
  // more rows behind the cut.
  const [leaderboardExpanded, setLeaderboardExpanded] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestProp?.id]);

  // Fetch leaderboard and timeline in parallel — they're independent and the
  // user wants both rendered at the same time. We surface errors per
  // request so a flaky timeline doesn't blank out the leaderboard.
  const fetchAll = async () => {
    setLoading(true);
    const params = { store_id: homeStore, contest_id: contestProp.id };
    const fetchBoth = () =>
      Promise.allSettled([
        getRightworkAPI(API_get_contest_leaderboard, params),
        getRightworkAPI(API_get_contest_timeline, params),
      ]);
    let [lbResult, tlResult] = await fetchBoth();
    // The two requests can straddle a scheduled refresh. If they came back
    // for different refreshes (different last_computed_at), the chart
    // wouldn't match the standings, so fetch both once more.
    if (resultsFromDifferentRefreshes(lbResult, tlResult)) {
      [lbResult, tlResult] = await fetchBoth();
    }
    if (lbResult.status === "fulfilled") {
      const payload = lbResult.value.data?.redux?.payload || {};
      if (payload.contest) setContest(payload.contest);
      setLeaderboard(payload.leaderboard || []);
      setYourPosition(payload.your_position || null);
    } else {
      toast.error("Error loading leaderboard", { autoClose: 3000 });
    }
    if (tlResult.status === "fulfilled") {
      const payload = tlResult.value.data?.redux?.payload || {};
      // Backend returns `timeline: null` for percentage metrics; we treat
      // that as a real null and the chart/replay components render nothing.
      setTimeline(payload.timeline);
    } else {
      // Don't toast — the leaderboard is the primary view, charts are
      // secondary. Just log so a persistent failure shows up.
      // eslint-disable-next-line no-console
      console.error("Failed to load contest timeline", tlResult.reason);
      setTimeline(null);
    }
    setLoading(false);
  };

  // No early loading return: the hero renders immediately from the contest
  // object the list already handed us (name, dates, metric, pills), and only
  // the data sections below it show a placeholder while the leaderboard +
  // timeline requests run. The old full-screen spinner both blanked the page
  // during slow recomputes and — because its h-screen block overflowed the
  // navbar'd page — toggled the scrollbar on and off, shifting the layout.
  if (!contest) return null;

  const format = contest.metric_format;
  const isStoreContest = contest.contest_type === "store_vs_store";

  // Normalize the leaderboard rows for store-vs-store contests into the same
  // shape the existing renderers expect (store_team_member with full_name +
  // store_name). Each row's "subject" becomes a synthetic STM whose
  // ``full_name`` is the store name; ``store_name`` is null so the row
  // doesn't render a redundant sub-label (the name itself IS the store).
  //
  // For individual contests we ALSO pass through the normalizer instead of
  // re-using the raw payload — that lets us pre-apply ``formatName`` once
  // here so child components can render the display name directly. The
  // alternative (each component calling formatName) silently mangles store
  // names like "PMQ Brooklyn" → "Pmq Brooklyn" because formatName title-
  // cases blindly; we don't want that for stores and there's no clean way
  // to opt-out per call site.
  const normalizedLeaderboard = isStoreContest
    ? leaderboard.map((row) => {
        // Targeted contests label the row by its GM (when set) with the store
        // as a sub-label; other store-vs-store metrics use the store name as
        // the subject itself. Store names are user-typed and may include
        // all-caps brand tokens; pass through untouched (no formatName).
        const gm = (row.store?.gm_label || "").trim();
        return {
          rank: row.rank,
          metric_value: row.metric_value,
          // Ratio components (store ratio-vs-target metrics) — carried
          // through so the grayed caption renders for store rows too; null
          // for every other store metric and the caption self-hides.
          numerator_value: row.numerator_value,
          denominator_value: row.denominator_value,
          is_winner: row.is_winner,
          store_team_member: {
            id: row.store?.id,
            full_name: gm || row.store?.store_name,
            store_id: row.store?.id,
            store_name: gm ? row.store?.store_name : null,
          },
        };
      })
    : leaderboard.map((row) => ({
        ...row,
        store_team_member: row.store_team_member
          ? {
              ...row.store_team_member,
              full_name: formatName(row.store_team_member.full_name),
            }
          : row.store_team_member,
      }));

  // Same normalization for the timeline. Backend returns `timeline.stores`
  // for store-vs-store contests; remap to `timeline.employees` so the chart
  // / replay / moments components keep working unchanged. ``store_name``
  // is nulled out for the same "name is the store" reason. Employee names
  // get a one-shot ``formatName`` here too so downstream components don't
  // need to (and won't accidentally re-format anything).
  let normalizedTimeline = null;
  if (timeline && isStoreContest && Array.isArray(timeline.stores)) {
    const timelineKind = timeline.kind || "cumulative";
    const isTargeted = timelineKind === "targeted_delta";
    normalizedTimeline = {
      dates: timeline.dates,
      kind: timelineKind,
      employees: timeline.stores.map((s) => {
        const gm = (s.gm_label || "").trim();
        return {
          id: s.id,
          // Targeted contests label each store by its GM (when set) with the
          // store name as a sub-label; other store-vs-store metrics use the
          // store name as the subject itself.
          full_name: isTargeted && gm ? gm : s.store_name,
          store_id: s.id,
          store_name: isTargeted && gm ? s.store_name : null,
          // ``ratio`` (target race), ``delta`` (targeted target race), or
          // ``daily``/``cumulative`` (bar race) will be populated depending on
          // metric kind; downstream viz picks the field it needs.
          daily: s.daily,
          cumulative: s.cumulative,
          ratio: s.ratio,
          delta: s.delta,
          target_value: s.target_value,
        };
      }),
    };
  } else if (timeline && Array.isArray(timeline.employees)) {
    normalizedTimeline = {
      ...timeline,
      employees: timeline.employees.map((e) => ({
        ...e,
        full_name: formatName(e.full_name),
      })),
    };
  } else {
    normalizedTimeline = timeline;
  }

  // Flat-list leaderboard. The medal-colored rank pills on the top 3 carry
  // the podium-tier semantics without needing a separate three-column block,
  // which doesn't fit at 1/3 width when paired with the race replay.
  const LEADERBOARD_VISIBLE_DEFAULT = 8;
  const visibleRows = leaderboardExpanded
    ? normalizedLeaderboard
    : normalizedLeaderboard.slice(0, LEADERBOARD_VISIBLE_DEFAULT);
  const hiddenCount = normalizedLeaderboard.length - visibleRows.length;
  const canToggleExpand = hiddenCount > 0 || leaderboardExpanded;

  // Individual multi-store contests: append the store name to each employee
  // in the visualizations so same-named workers at different locations
  // don't blur together. Store-vs-store contests never need the tag — the
  // subject's display name is already the store name.
  const showStoreTag =
    !isStoreContest && (contest.store_ids?.length || 0) > 1;

  // Leaderboard subject sub-label. Individual multi-store contests use
  // `showStoreTag` (disambiguate same-named workers). Targeted store-vs-store
  // contests relabel each row to its GM and stash the location in `store_name`,
  // so the leaderboard needs the tag on to surface which location a GM
  // represents — mirroring the target race. Keyed off the contest flag (not the
  // timeline) so it holds even when there's no timeline to render.
  const requiresStoreTargets = Boolean(contest.requires_store_targets);
  const leaderboardShowStoreTag = requiresStoreTargets || showStoreTag;

  // Direction flag for the visualization components — without this, the
  // race replay / cumulative chart tooltip / moments derivation all rank
  // descending by default, which is wrong for lower-is-better metrics
  // (late_clockins, missed_shifts) and would crown the wrong leader.
  const lowerIsBetter = contest.metric_direction === "lower_is_better";
  // Ratio metrics use a separate visualization that places the marker
  // relative to a 100% target instead of growing rightward from zero.
  const isRatioTimeline = normalizedTimeline?.kind === "ratio";
  // Per-store-target metrics (Labor % vs Target) use the same target race but
  // centered on 0 (the "on target" line) with a signed percentage-point delta.
  const isTargetedDelta = normalizedTimeline?.kind === "targeted_delta";
  const usesTargetRace = isRatioTimeline || isTargetedDelta;
  // Goal metrics (total vs each location's goal) chart percent of goal, so
  // the race and chart draw a goal line at 100%.
  const isGoalMetric = contest.metric_format === "goal_percent";

  return (
    <div className="w-full px-6 mt-4 pb-16">
      {/* Top action bar */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={onBack}
          className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-1 px-2 py-1 -ml-2 rounded-lg"
        >
          <ChevronLeftIcon style={{ fontSize: 18 }} />
          <span>Back</span>
        </button>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {/* Duplicate sits to the left of Edit so the destructive-er
                action (Edit, which can lead to Delete) stays in the
                right-most "primary action" slot. Shown for ALL contest
                types: store-vs-store sources get the modal's rerun-only
                mode (same locations, new dates); rollout mode remains
                individual-only and the modal hides its toggle for SVS.
                We deliberately do NOT gate on a "has eligible target"
                heuristic. `storeNames` is the cross-company member
                stores prop, so any heuristic built from it can show
                the button when there are no SAME-company targets
                — leading to dead-end UX. The duplicate modal already
                fires its own company-scoped eligible-stores fetch and
                renders an explicit "this contest already covers every
                location you can access" empty state when there's
                nothing to clone to. */}
            <button
              onClick={() => setDuplicateOpen(true)}
              className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium flex items-center gap-1.5"
            >
              <ContentCopyIcon style={{ fontSize: 14 }} />
              Duplicate
            </button>
            <button
              onClick={() => onEdit(contest)}
              className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium flex items-center gap-1.5"
            >
              <EditIcon style={{ fontSize: 14 }} />
              Edit contest
            </button>
          </div>
        )}
      </div>

      {/* Duplicate modal — rendered at the detail level so the action
          bar above can trigger it. Closing with `refresh=true` (after
          a successful duplicate) routes back to the list so the user
          can see the new contests; closing with `refresh=false`
          (Cancel) leaves them on this detail page. */}
      <ContestDuplicateModal
        isOpen={duplicateOpen}
        onClose={(refresh) => {
          setDuplicateOpen(false);
          if (refresh) onBack(true);
        }}
        contest={contest}
        storeNames={storeNames}
        homeStore={homeStore}
      />

      {/* Compact single-card hero. Status pill + live countdown chip on top
          row, title + description below, and a horizontal stat strip — all
          the metadata that used to live in three stacked cards (hero +
          countdown + rewards). Status is the only color up top; the rest is
          monochrome to keep the page from competing with itself. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            {contest.is_draft && <DraftBadge />}
            <StatusPill status={contest.status} daysRemaining={null} />
            <HeroTypeChip contestType={contest.contest_type} />
          </div>
          {/* Right column: countdown on top, progress bar below. Stacking
              them fills the previously-empty space under the countdown
              with a useful "how far along are we" signal. */}
          <div className="flex flex-col items-end gap-2">
            <HeroCountdownInline contest={contest} />
            <HeroProgressBar contest={contest} />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-slate-900 leading-tight">
          {contest.name}
        </h1>
        {contest.description && (
          <p className="text-sm text-slate-600 mt-1.5 max-w-3xl leading-relaxed">
            {contest.description}
          </p>
        )}

        {(() => {
          // Participants stat: count the actual leaderboard rows (real ranked
          // entries). Subtext now surfaces the actual store names (truncated
          // with "+N more"), and for picked individual rosters the stat value
          // gets a hover tooltip listing the picked employees so admins can
          // confirm who's competing without opening the edit form.
          const lbCount = normalizedLeaderboard.length;
          const storeNames = contest.store_names || [];
          const pickedDetails = contest.participant_details || [];
          const pickedCount = pickedDetails.length;

          // Track truncation so we can decide whether the "full list on
          // hover" tooltip is meaningful — if every store name already
          // fits, the tooltip would just echo the visible subtext.
          const STORES_MAX_VISIBLE = 3;
          const storesTruncated = storeNames.length > STORES_MAX_VISIBLE;
          const subtextFromStores = (() => {
            if (storeNames.length === 0) return null;
            const visible = storeNames.slice(0, STORES_MAX_VISIBLE);
            return storesTruncated
              ? `${visible.join(" · ")} · +${storeNames.length - visible.length} more`
              : visible.join(" · ");
          })();

          let participantsSubtext = null;
          if (isStoreContest) {
            // Store-vs-store: subtext IS the store names — the count above
            // already conveys "5 locations competing."
            participantsSubtext = subtextFromStores;
          } else {
            participantsSubtext = subtextFromStores;
            if (pickedCount > 0) {
              participantsSubtext = participantsSubtext
                ? `${participantsSubtext} · ${pickedCount} picked`
                : `${pickedCount} picked`;
            }
          }

          // Roster tooltip — only meaningful for individual contests with an
          // explicit pick. Capped at 10 names so the tooltip fits in a
          // typical viewport without forcing a scroll — scrollable tooltips
          // are unreachable in practice (the tooltip dismisses the moment
          // the cursor leaves the trigger). Admins who need the full list
          // can open the edit form.
          const ROSTER_TOOLTIP_CAP = 10;
          let rosterTooltip = null;
          if (!isStoreContest && pickedCount > 0) {
            const visiblePicks = pickedDetails.slice(0, ROSTER_TOOLTIP_CAP);
            const overflow = pickedCount - visiblePicks.length;
            rosterTooltip = (
              <div className="text-xs">
                <div className="font-semibold mb-1">Picked roster</div>
                <ul className="space-y-0.5">
                  {visiblePicks.map((p) => (
                    <li key={p.id}>
                      {formatName(p.full_name)}
                      {p.store_name && showStoreTag && (
                        <span className="text-slate-300 ml-1">
                          ({p.store_name})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {overflow > 0 && (
                  <div className="text-slate-300 mt-1">
                    +{overflow} more — open the edit form to see the full list
                  </div>
                )}
              </div>
            );
          }

          const stats = [
            { label: "Metric", value: contest.metric_label || "—" },
            {
              label: "Duration",
              value: `${formatDateReadable(contest.start_date)} – ${formatDateReadable(contest.end_date)}`,
            },
            {
              label: isStoreContest ? "Locations" : "Participants",
              // Leaderboard-derived — show a dash instead of a false "0"
              // while the fetch is in flight.
              value: loading ? "—" : String(lbCount),
              subtext: participantsSubtext,
              // Only attach the full-list tooltip when the visible subtext
              // is actually truncated; otherwise hovering would show the
              // same store list that's already on screen.
              subtextFullText: storesTruncated ? storeNames.join(", ") : null,
              valueTooltip: rosterTooltip,
            },
            {
              label: "Winners",
              // Hit-target contests have no fixed count — show how many
              // have hit so far (final count once Completed). Leaderboard-
              // derived, so dash while loading like the count above; an
              // Upcoming contest has no leaderboard yet, so just name the
              // rule rather than a misleading "0 hit target".
              value: !isHitTargetContest(contest)
                ? winnersLabel(contest)
                : contest.status === "Upcoming"
                ? "Hit target"
                : loading
                ? "—"
                : `${normalizedLeaderboard.filter((r) => r.is_winner).length} hit target`,
            },
          ];
          if (contest.reward_description) {
            stats.push({
              label: "Rewards",
              value: contest.reward_description,
              allowMultiLine: true,
            });
          }
          // grid-cols tracks stat count so an empty 5th column doesn't gap.
          const desktopCols = stats.length === 5 ? "grid-cols-5" : "grid-cols-4";
          return (
            <div
              className={`mt-4 pt-4 border-t border-slate-100 grid ${desktopCols} lg:grid-cols-2 md:grid-cols-1 gap-4`}
            >
              {stats.map((s) => (
                <HeroStat key={s.label} {...s} />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Data sections. While the leaderboard + timeline requests are in
          flight, render one fixed-height placeholder card in their place —
          the hero above stays fully interactive, and the stable height
          avoids the scrollbar popping in/out (the old full-screen spinner's
          layout shift). */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white h-96 flex items-center justify-center mb-3">
          <LoadSpinner />
        </div>
      ) : (
        <>
      {/* Your standing — promoted card when applicable. Backend only sets
          ``your_position`` for individual contests where the requesting
          user is in the leaderboard, so this card naturally never renders
          for store-vs-store contests. */}
      {yourPosition && (
        <div className="rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 to-white p-4 mb-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center">
              <GpsFixedIcon style={{ fontSize: 18 }} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-violet-700 font-semibold">
                Your standing
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-xl font-bold text-slate-900">
                  #{yourPosition.rank}
                </span>
                <span className="text-xs text-slate-500">
                  of {normalizedLeaderboard.length}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
              Score
            </div>
            <div className="text-xl font-bold text-slate-900 mt-0.5">
              {formatMetricValue(yourPosition.metric_value, format)}
            </div>
            <RatioComponentsCaption
              row={yourPosition}
              numeratorLabel={contest.metric_numerator_label}
              denominatorLabel={contest.metric_denominator_label}
              format={format}
            />
          </div>
        </div>
      )}

      {/* Leaderboard + Race Replay pair. The two views show the same data
          in different formats (snapshot vs. animation), so we pair them
          horizontally — leaderboard at 1/3 for at-a-glance ranking, race
          replay at 2/3 where the bars have room to breathe. When there's no
          timeline (percentage metrics), the leaderboard falls back to full
          width since there's nothing to its right. */}
      {normalizedTimeline ? (
        <div className="grid grid-cols-3 lg:grid-cols-1 gap-3 mb-3">
          <div className="col-span-1">
            <LeaderboardCard
              contest={contest}
              leaderboard={normalizedLeaderboard}
              visibleRows={visibleRows}
              yourPosition={yourPosition}
              format={format}
              canToggleExpand={canToggleExpand}
              leaderboardExpanded={leaderboardExpanded}
              setLeaderboardExpanded={setLeaderboardExpanded}
              hiddenCount={hiddenCount}
              showStoreTag={leaderboardShowStoreTag}
            />
          </div>
          <div className="col-span-2 lg:col-span-1">
            {usesTargetRace ? (
              <ContestTargetRace
                timeline={normalizedTimeline}
                // Targeted contests show the store beneath the GM name; ratio
                // contests keep the store-tag rule for multi-store individual
                // races (always false for store-vs-store).
                showStoreTag={isTargetedDelta ? true : showStoreTag}
                direction={contest.metric_direction}
                winnersCount={contest.winners_count}
                winnerMode={contest.winner_mode}
                // Delta metrics center the track on 0 ("on target") and read
                // the signed ``delta`` series in percentage points; ratio
                // metrics keep the 100% (1.0) center and the ``ratio`` series.
                centerValue={isTargetedDelta ? 0 : 1.0}
                seriesKey={isTargetedDelta ? "delta" : "ratio"}
                valueFormat={isTargetedDelta ? "points_delta" : "percentage"}
                minHalfWindow={isTargetedDelta ? 1.0 : 0.05}
                subtitle={
                  isTargetedDelta
                    ? isHitTargetContest(contest)
                      ? contest.metric_direction === "higher_is_better"
                        ? "Everyone at or over target wins"
                        : "Everyone at or under target wins"
                      : contest.metric_direction === "higher_is_better"
                      ? "Furthest over target wins"
                      : "Furthest under target wins"
                    : "Closest to 100% wins"
                }
                // Delta metrics get diverging green/red bars from the target
                // line, with the good side picked by the metric's direction
                // (labor: under = good; sales ratios: over = good); ratio
                // metrics keep the two-sided marker (neither side is "good").
                variant={isTargetedDelta ? "bar" : "marker"}
              />
            ) : (
              <ContestRaceReplay
                timeline={normalizedTimeline}
                metricFormat={contest.metric_format}
                metricLabel={contest.metric_label}
                showStoreTag={showStoreTag}
                lowerIsBetter={lowerIsBetter}
                winnersCount={contest.winners_count}
                winnerMode={contest.winner_mode}
                goalValue={isGoalMetric ? GOAL_PERCENT : null}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="mb-3">
          <LeaderboardCard
            contest={contest}
            leaderboard={normalizedLeaderboard}
            visibleRows={visibleRows}
            yourPosition={yourPosition}
            format={format}
            canToggleExpand={canToggleExpand}
            leaderboardExpanded={leaderboardExpanded}
            setLeaderboardExpanded={setLeaderboardExpanded}
            hiddenCount={hiddenCount}
            showStoreTag={leaderboardShowStoreTag}
          />
        </div>
      )}

      {/* Time-series narrative — 4-column grid: the cumulative chart takes
          half the width (it's the highest-value visualization and needs room
          to breathe), and Key Moments + Biggest Movers each take a quarter.
          Each card self-hides if its derivation is empty. Collapses to single
          column on tablet/phone.

          Hidden for ratio-kind timelines: a cumulative line for a ratio
          metric would just be the same target-vs-actual story the Target
          Race already shows, and "biggest mover" is ill-defined when the
          ranking metric is distance to a fixed point. */}
      {normalizedTimeline && !usesTargetRace && (
        <div className="grid grid-cols-4 lg:grid-cols-1 gap-3">
          <div className="col-span-2 lg:col-span-1">
            <ContestCumulativeChart
              timeline={normalizedTimeline}
              metricFormat={contest.metric_format}
              metricLabel={contest.metric_label}
              showStoreTag={showStoreTag}
              lowerIsBetter={lowerIsBetter}
              goalValue={isGoalMetric ? GOAL_PERCENT : null}
            />
          </div>
          <div className="col-span-1">
            <ContestKeyMoments
              timeline={normalizedTimeline}
              showStoreTag={showStoreTag}
              lowerIsBetter={lowerIsBetter}
            />
          </div>
          <div className="col-span-1">
            <ContestBiggestMovers
              timeline={normalizedTimeline}
              showStoreTag={showStoreTag}
              lowerIsBetter={lowerIsBetter}
            />
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
